import React, { useState, useEffect } from 'react'
import styled from 'styled-components'

import { graphQLQuery } from '@/hooks/hooks'
import useUser from '@/hooks/useUser'
import type { Applicant } from '@/lib/apollo/query/get-applicant'
import QUERIES from '@/lib/graphql'
import type { Application } from '@/types/Activity'
import type { Touchpoint } from '@/types/Touchpoints'
import { Role } from '@/types/User'
import { REDUCED_JOURNEY_SUBJECTS } from '@/utils/constants'
import { hasRole } from '@/utils/role'

import BotAccess from './BotAccess'
import Developer from './DeveloperTools'
import Escalation from './Escalation'
import Fraud from './Fraud'
import InternalComments from './InternalComments'
import Notifications from './Notifications'
import Requests from './Requests'
import ResetSections from './ResetSections'
import ViewReport from './ViewReport'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 20px;
  align-items: center;
`

interface Props {
  application: Application
  applicant: Applicant
  onApplicantUpdated: () => void
  isRestrictedReference: boolean
  requestsSidebarOpen: boolean
  setRequestsSidebarOpen: (open: boolean) => void
}

const ActionButtons = ({
  application,
  applicant,
  onApplicantUpdated,
  isRestrictedReference,
  requestsSidebarOpen,
  setRequestsSidebarOpen,
}: Props) => {
  const [messages, setMessages] = useState<Touchpoint[]>([])
  const [error, setError] = useState(true)
  const { token, isDeveloperModeEnabled, roles } = useUser()

  const showResetSectionsAction = hasRole(roles, [
    Role.resetSection,
    Role.labResetResidential,
    Role.supervisor,
    Role.admin,
  ])

  function fetchInternalTouchpoints() {
    graphQLQuery({
      query: QUERIES.getInternalTouchpoints,
      variables: {
        customerId: application.customerId,
        applicationId: application.applicationId,
        subjectId: application.subjectId,
      },
      customHeaders: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(resData => {
        return resData.json()
      })
      .then(({ data }) => {
        if (data && data.getInternalTouchpoints) {
          setMessages(data.getInternalTouchpoints)
          // Scroll to include the new message in the view
          const msgBoxContainer = document.getElementById('messages-container')
          if (msgBoxContainer) {
            msgBoxContainer.scrollTop = msgBoxContainer.scrollHeight
          }
          if (error) {
            setError(false)
          }
        } else {
          setError(true)
        }
      })
      .catch(err => {
        setError(true)
        console.log(err)
      })
  }

  useEffect(() => {
    fetchInternalTouchpoints()
  }, [application]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container>
      {!REDUCED_JOURNEY_SUBJECTS.includes(application.type) && (
        <>
          <Escalation applicant={applicant} onApplicantUpdated={onApplicantUpdated} />
          <Fraud applicant={applicant} />
          <BotAccess
            referenceTier={applicant.referenceTier}
            applicationId={applicant.applicationId}
            customerId={applicant.customerId}
            subjectId={applicant.id}
          />
          <Requests
            applicant={applicant}
            application={application}
            disabled={isRestrictedReference}
            isOpen={requestsSidebarOpen}
            setOpen={setRequestsSidebarOpen}
          />
          <Notifications application={application} />
          <InternalComments
            application={application}
            messages={messages}
            onPostCommentSuccess={fetchInternalTouchpoints}
          />
          <ViewReport referenceTier={applicant.referenceTier} application={application} />
          {showResetSectionsAction && (
            <ResetSections
              application={application}
              customerId={applicant.customerId}
              applicationId={applicant.applicationId}
              subjectId={applicant.id}
              disabled={isRestrictedReference}
            />
          )}
          {isDeveloperModeEnabled && (
            <Developer
              customerId={applicant.customerId}
              applicationId={applicant.applicationId}
              subjectId={applicant.id}
              supportContacts={applicant.supportContacts}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default ActionButtons
